// Lib
import React from 'react';
import PropTypes from 'prop-types';

// Constants
import { GRID } from '../../../utils/grid/gridConstants';
import {
    SKETCH_MIN_WIDTH,
    SKETCH_MIN_HEIGHT,
    SKETCH_CANVAS_PIXEL_RATIO,
    SKETCH_DEFAULT_WIDTH,
} from '../../../../common/drawings/sketches/sketchConstants';
import { getDrawingSvg, getElementId } from '../../../../common/elements/utils/elementPropertyUtils';

const SNAP_THRESHOLD = 2;
const SNAP_TO_GRID_OFFSET = 2;

export const calculateStaticHeightGridUnits = (elNode, emptyDrawingNode, gridSize) => {
    if (!elNode || !emptyDrawingNode) return 0;

    const elRect = elNode.getBoundingClientRect();
    const emptyDrawingRect = emptyDrawingNode.getBoundingClientRect();

    return (elRect.height - emptyDrawingRect.height) / gridSize;
};

export default (DecoratedComponent) => {
    class SketchResizeDecorator extends React.Component {
        constructor(props) {
            super(props);

            this.drawingElementRef = React.createRef();
            this.emptyDrawingRef = React.createRef();
        }

        setElementSizeAndPosition = (size, position) => {
            if (!size) {
                this.props.setElementSizeAndPosition(null, position);
                return;
            }

            const { gridSize, isMultiResizingOrAnimating, element } = this.props;

            let width = Math.max(Math.round(size.width), SKETCH_MIN_WIDTH);
            const height = Math.max(Math.round(size.height), SKETCH_MIN_WIDTH);

            const sizeChanges = { width, height };

            if (!isMultiResizingOrAnimating) {
                // Implement width snapping for sketches
                const defaultWidthDiff = Math.abs(width - SKETCH_DEFAULT_WIDTH);
                if (defaultWidthDiff <= SNAP_THRESHOLD) {
                    width = SKETCH_DEFAULT_WIDTH;
                    sizeChanges.width = SKETCH_DEFAULT_WIDTH;
                }
            }

            this.props.setElementSizeAndPosition(sizeChanges, position);

            // We don't want to override the drawing aspect ratio of a sketch that has content
            if (getDrawingSvg(element)) return;

            const staticHeight = calculateStaticHeightGridUnits(
                this.drawingElementRef.current,
                this.emptyDrawingRef.current,
                gridSize,
            );

            // Increase the resolution to improve iPad behaviour
            const drawingWidth = width * GRID.LARGE.size;
            const drawingHeight =
                Math.max(SKETCH_MIN_HEIGHT, (height - staticHeight) * GRID.LARGE.size) - SNAP_TO_GRID_OFFSET;

            this.props.dispatchUpdateElement({
                id: getElementId(element),
                changes: {
                    drawing: {
                        width: drawingWidth * SKETCH_CANVAS_PIXEL_RATIO,
                        height: drawingHeight * SKETCH_CANVAS_PIXEL_RATIO,
                    },
                },
            });
        };

        setTempElementSize = (size) => {
            const { setTempElementSize, isMultiResizingOrAnimating } = this.props;

            if (!size) {
                setTempElementSize(size);
                return;
            }

            const adjustedSize = {
                width: size.width,
                height: Math.max(SKETCH_MIN_HEIGHT, size.height),
            };

            if (!isMultiResizingOrAnimating) {
                // Implement width snapping for sketches
                const defaultWidthDiff = Math.abs(size.width - SKETCH_DEFAULT_WIDTH);

                if (defaultWidthDiff <= SNAP_THRESHOLD) {
                    adjustedSize.width = SKETCH_DEFAULT_WIDTH;
                }
            }

            setTempElementSize(adjustedSize);
        };

        handleDoubleClick = (event) => {
            event.preventDefault();
            event.stopPropagation();

            // Might need to keep track of the scaling for annotation drawings
            return this.setElementSizeAndPosition({ width: SKETCH_DEFAULT_WIDTH });
        };

        render() {
            return (
                <DecoratedComponent
                    {...this.props}
                    drawingElementRef={this.drawingElementRef}
                    emptyDrawingRef={this.emptyDrawingRef}
                    setTempElementSize={this.setTempElementSize}
                    setElementSizeAndPosition={this.setElementSizeAndPosition}
                    handleDoubleClick={this.handleDoubleClick}
                />
            );
        }
    }

    SketchResizeDecorator.propTypes = {
        element: PropTypes.object,
        gridSize: PropTypes.number,
        dispatchUpdateElement: PropTypes.func,
        setElementSizeAndPosition: PropTypes.func,
        setTempElementSize: PropTypes.func,
        isMultiResizingOrAnimating: PropTypes.bool,
    };

    return SketchResizeDecorator;
};
